import { useState, useEffect, useCallback } from "react";
import { Loader2, AlertCircle, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { BadgeVenture } from "@/components/ui/badge-venture";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useStageMapping } from "@/hooks/useStageMapping";
import {
  VENTURE_CATEGORIES,
  CATEGORY_LABELS,
  CATEGORY_BADGE_VARIANTS,
  type VentureCategory,
} from "@/lib/stage-categories";

interface StageMappingModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  clientId: string;
  clientName: string;
}

interface GhlStage {
  id: string;
  name: string;
  position: number;
}

interface GhlPipeline {
  id: string;
  name: string;
  stages: GhlStage[];
}

interface LocalMapping {
  ghlStageId: string;
  ghlStageName: string;
  ghlPipelineId: string;
  ghlPipelineName: string;
  category: VentureCategory;
}

export function StageMappingModal({
  open,
  onOpenChange,
  clientId,
  clientName,
}: StageMappingModalProps) {
  const [pipelines, setPipelines] = useState<GhlPipeline[]>([]);
  const [localMappings, setLocalMappings] = useState<LocalMapping[]>([]);
  const [fetchLoading, setFetchLoading] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const { mappings: existingMappings, isLoading: mappingsLoading, invalidate } =
    useStageMapping(clientId);

  const fetchPipelines = useCallback(async (signal: AbortSignal) => {
    setFetchLoading(true);
    setFetchError(null);
    try {
      const { data, error } = await supabase.functions.invoke("ghl-pipelines", {
        body: { clientId },
      });
      if (signal.aborted) return;
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      setPipelines(data.pipelines ?? []);
    } catch (error: unknown) {
      if (signal.aborted) return;
      const message =
        error instanceof Error ? error.message : "Failed to fetch GHL pipelines";
      setFetchError(message);
      toast.error("Failed to load pipelines", { description: message });
    } finally {
      if (!signal.aborted) setFetchLoading(false);
    }
  }, [clientId]);

  // Fetch pipelines when modal opens
  useEffect(() => {
    if (open) {
      const controller = new AbortController();
      fetchPipelines(controller.signal);
      return () => controller.abort();
    } else {
      // Reset state on close
      setPipelines([]);
      setLocalMappings([]);
      setFetchError(null);
    }
  }, [open, fetchPipelines]);

  // Build local mappings once pipelines and existing mappings are loaded
  useEffect(() => {
    if (pipelines.length === 0) return;

    const mapped: LocalMapping[] = [];
    for (const pipeline of pipelines) {
      for (const stage of pipeline.stages) {
        const existing = existingMappings.find(
          (m) => m.ghl_stage_id === stage.id
        );
        mapped.push({
          ghlStageId: stage.id,
          ghlStageName: stage.name,
          ghlPipelineId: pipeline.id,
          ghlPipelineName: pipeline.name,
          category: existing?.venture_category ?? "unmapped",
        });
      }
    }
    setLocalMappings(mapped);
  }, [pipelines, existingMappings]);

  const handleCategoryChange = (stageId: string, category: VentureCategory) => {
    setLocalMappings((prev) =>
      prev.map((m) =>
        m.ghlStageId === stageId ? { ...m, category } : m
      )
    );
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const rows = localMappings.map((m, index) => ({
        client_id: clientId,
        ghl_stage_id: m.ghlStageId,
        ghl_stage_name: m.ghlStageName,
        ghl_pipeline_id: m.ghlPipelineId,
        ghl_pipeline_name: m.ghlPipelineName,
        venture_category: m.category,
        sort_order: index,
      }));

      const { error } = await supabase
        .from("stage_mappings")
        .upsert(rows, { onConflict: "client_id,ghl_stage_id" });

      if (error) throw error;

      invalidate();
      toast.success("Stage mappings saved");
      onOpenChange(false);
    } catch (error: unknown) {
      const message =
        error instanceof Error ? error.message : "Failed to save mappings";
      toast.error("Failed to save mappings", { description: message });
    } finally {
      setSaving(false);
    }
  };

  const isLoading = fetchLoading || mappingsLoading;
  const hasStages = localMappings.length > 0;
  const mappedCount = localMappings.filter((m) => m.category !== "unmapped").length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Map GHL Stages — {clientName}</DialogTitle>
          {hasStages && (
            <p className="text-sm text-muted-foreground">
              {mappedCount} of {localMappings.length} stages mapped
            </p>
          )}
        </DialogHeader>

        <div className="flex-1 overflow-y-auto min-h-0 py-2">
          {isLoading && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              <span className="ml-2 text-sm text-muted-foreground">
                Loading pipelines...
              </span>
            </div>
          )}

          {fetchError && !isLoading && (
            <div className="flex flex-col items-center justify-center py-12 gap-3">
              <AlertCircle className="h-8 w-8 text-destructive" />
              <p className="text-sm text-destructive text-center">{fetchError}</p>
              <Button variant="outline" size="sm" onClick={() => fetchPipelines(new AbortController().signal)}>
                <RefreshCw className="h-4 w-4 mr-1" />
                Retry
              </Button>
            </div>
          )}

          {!isLoading && !fetchError && !hasStages && (
            <p className="text-sm text-muted-foreground text-center py-12">
              No pipelines or stages found in GHL.
            </p>
          )}

          {!isLoading && hasStages && (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left">
                  <th className="pb-2 font-medium text-muted-foreground">Stage Name</th>
                  <th className="pb-2 font-medium text-muted-foreground">Pipeline</th>
                  <th className="pb-2 font-medium text-muted-foreground">VentureOS Category</th>
                </tr>
              </thead>
              <tbody>
                {localMappings.map((m) => (
                  <tr key={m.ghlStageId} className="border-b last:border-0">
                    <td className="py-2 pr-3">{m.ghlStageName}</td>
                    <td className="py-2 pr-3 text-muted-foreground">
                      {m.ghlPipelineName}
                    </td>
                    <td className="py-2">
                      <Select
                        value={m.category}
                        onValueChange={(val) =>
                          handleCategoryChange(m.ghlStageId, val as VentureCategory)
                        }
                      >
                        <SelectTrigger className="w-[180px] h-8">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {VENTURE_CATEGORIES.map((cat) => (
                            <SelectItem key={cat} value={cat}>
                              <span className="flex items-center gap-2">
                                <BadgeVenture variant={CATEGORY_BADGE_VARIANTS[cat]}>
                                  {CATEGORY_LABELS[cat]}
                                </BadgeVenture>
                              </span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={isLoading || !hasStages || saving}
          >
            {saving && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
            Save Mappings
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
